import { Badge } from "@components/common/badge";
import { ColumnDef } from "@tanstack/react-table";
import { DataTableColumnHeader } from "@components/common/data-table/data-table-column-header";
import { Eye, Folder, FolderKanban, Folders, FolderTree, Info, LinkIcon } from "lucide-react";
import { DictType } from "@pma/types";
import { Link } from "react-router-dom";
import { PROJECTS_PAGE_ROUTE } from "@pma/utils/page-routes";
import { Button } from "@components/common/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/common/tooltip";
import { useAuth } from "@stores/auth-store";
import { useModal } from "@stores/modal-store";
import useTranslator from "@hooks/use-translator";
import { getSubFoldersCount } from "@pma/utils/helpers";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../common/hover-card";

const formatFolderSize = (size: number) => {
  if (size === 0) {
    return "0 KB";
  }

  const kb = size / 1000;
  if (kb < 1000) {
    return `${Math.round(kb)} KB`;
  }

  const mb = kb / 1000;
  if (mb < 1000) {
    return `${mb.toFixed(2)} MB`;
  }

  const gb = mb / 1000;
  return `${gb.toFixed(2)} GB`;
};

const formatTimestamp = (timestamp: number, locale: string = "en-US") => {
  const date = new Date(timestamp);
  return date.toLocaleDateString(locale === "he" ? "he-IL" : "en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const filesColumn: ColumnDef<any>[] = [
  {
    accessorKey: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Name" className="" />
    ),
    cell: ({ row }) => {
      const title: string = row.getValue("name");
      const { total, data } = getSubFoldersCount(row.original)
      const levelCount = Object.keys(data).length
      const { setActiveFolder } = useAuth();
      const modal = useModal();
    
      let IconComponent
      if (total === 0) {
        IconComponent = Folder
      } else if (levelCount === 2) {
        IconComponent = Folders
      } else {
        IconComponent = FolderTree
      }
      return (
        <div 
          className="items-center flex font-medium gap-2 max-w-[300px] cursor-pointer" 
          onClick={() => {
            modal.onOpen();
            modal.setContext("folder");
            setActiveFolder(row.original);
          }}
        >
          <IconComponent size={16} className="flex-shrink-0" /> 
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="truncate">{title || "---"}</span>
            </TooltipTrigger>
            <TooltipContent align="start">{title || "---"}</TooltipContent>
          </Tooltip>
        </div>
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: "sub",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Sub Folders" className="" />
    ),
    cell: ({ row }) => {
      const { translate } = useTranslator();
      const { total, dataStrArr } = getSubFoldersCount(row.original, translate);
      return (
        <div className="items-center font-medium flex gap-2">
          <Badge
            className="w-10 flex items-center justify-center"
            variant={total ? "default" : "outline"}
          >
            {total || "---"}
          </Badge>
          {total > 0 &&
            <HoverCard openDelay={0} closeDelay={100}>
              <HoverCardTrigger>
                <Info className="text-blue-500" size={14} />
              </HoverCardTrigger>
              <HoverCardContent className="flex flex-col items-start w-fit gap-2">
                {dataStrArr.map((str, index) => (
                  <p key={str + '' + index}>{str}</p>
                ))}
              </HoverCardContent>
            </HoverCard>
          }
        </div>
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: "folder_size",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Size" className="" />
    ),
    cell: ({ row }) => {
      const size: number = row.getValue("folder_size");
      return (
        <div className="font-medium">
          {size !== undefined ? formatFolderSize(size) : "---"}
        </div>
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: "last_update",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Last Updated" className="" />
    ),
    cell: ({ row }) => {
      const { locale } = useTranslator();
      const timestamp: number = row.getValue("last_update");
      return (
        <div className="font-medium">
          {timestamp ? formatTimestamp(timestamp, locale) : "---"}
        </div>
      );
    },
    enableSorting: false,
  },
  {
    id: "actions",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Actions" />
    ),
    cell: ({ row }) => {
      const { translate } = useTranslator();

      return (
        <div className="flex items-center gap-4">
          <Tooltip>
            <TooltipTrigger>
              <Link
                className="text-primary"
                to={PROJECTS_PAGE_ROUTE + `/${row.original.project_id}`}
              >
                <LinkIcon size={14} />
              </Link>
            </TooltipTrigger>
            <TooltipContent>{translate("Go to project")}</TooltipContent>
          </Tooltip>

          <PreviewFile folder={row.original} />
        </div>
      );
    },
  },
];

const PreviewFile = ({ folder }: { folder: DictType }) => {
  const { setActiveFolder } = useAuth();
  const modal = useModal();
  const { translate } = useTranslator();

  return (
    <div className="">
      <Tooltip>
        <TooltipTrigger>
          <Button
            variant={"ghost"}
            size={"icon"}
            onClick={() => {
              modal.onOpen();
              modal.setContext("folder");
              setActiveFolder(folder);
            }}
          >
            <Eye size={14} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{translate("Preview folder")}</TooltipContent>
      </Tooltip>
    </div>
  );
};
